import { useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from '@tmapy/redux';
import type { CallApiResult } from '@tmapy/beetlejuice-utils';

import { ResourceQueryResult } from '../redux/resource/types';
import { actionResourceLoad } from '../redux/resource/actions';
import { QUERY_STATE } from '../constants';
import { queryArgs, QueryArgs } from '../utils/queryArgs';

export type UseResource<ResourceType extends object> = ResourceQueryResult<ResourceType> & {
  refetch(): Promise<CallApiResult<ResourceType>>;
  fetchMore(newUrl: string): Promise<CallApiResult<ResourceType>>;
};

export type UseResourceProps = {
  path: string | null;
  apiUrl: string;
  query?: QueryArgs | null;
  useCached?: boolean;
};

const createEmptyResource = () => ({
  queryState: QUERY_STATE.EMPTY,
  data: null,
  fetchedAt: Date.now(),
});

export function useResource<ResourceType extends object = object>({
  path,
  query,
  apiUrl,
  useCached,
}: UseResourceProps): UseResource<ResourceType> {
  const dispatch = useDispatch();

  const [emptyResource, setEmptyResource] = useState<ResourceQueryResult<ResourceType>>();

  const url = useMemo(
    () => (path ? `${apiUrl}${path}${queryArgs(query)}` : null),
    [path, apiUrl, query],
  );

  const resource = useSelector(
    (state) =>
      (url && state.resource[url]
        ? state.resource[url]
        : emptyResource) as ResourceQueryResult<ResourceType>,
  );

  const refetch: () => Promise<CallApiResult<ResourceType>> = useCallback(async () => {
    if (url) {
      return dispatch<any>(actionResourceLoad<ResourceType>(url));
    }
    const emptyResource = createEmptyResource();
    setEmptyResource(emptyResource);
    return emptyResource;
  }, [dispatch, url]);

  const fetchMore: (newUrl: string) => Promise<CallApiResult<ResourceType>> = useCallback(
    async (newUrl) => {
      if (url) {
        return dispatch<any>(actionResourceLoad<ResourceType>(newUrl, url));
      }
    },
    [dispatch, url],
  );

  useEffect(() => {
    if (resource?.queryState === QUERY_STATE.WAITING) return;
    if (useCached && resource?.queryState === QUERY_STATE.SUCCESS) return;

    refetch();
  }, [
    refetch,
    useCached,
    // DO NOT add resource?.queryState,
  ]);

  return useMemo(
    () => ({
      ...resource,
      refetch,
      fetchMore,
    }),
    [resource, refetch, fetchMore],
  );
}
